import { useEffect, useRef, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { getServiceByRoute } from '../../data/services';
import { usePlanAccess } from '../../hooks/usePlanAccess';
import { useComingSoon } from '../../hooks/useComingSoon';
import { ServiceLandingPage } from '../../pages/servicos/ServiceLandingPage';
import { ph } from '../../lib/posthog';

// ─────────────────────────────────────────
// ServiceGate, wraps an authenticated service route.
//
// Resolution order for the current route:
//   1. No matching service in catalog → render children (pass-through)
//   2. Plan access check:
//        - 'loading'  → minimal loading skeleton
//        - 'allowed'  → render children
//        - 'denied'   → render ServiceLockedScreen inline (stays in AppLayout)
//   3. Coming soon → redirect to /servicos/{slug} landing page
//
// Admin users and users with unlimited_access always get 'allowed'.
// ─────────────────────────────────────────

export function ServiceGate({ children }: { children: ReactNode }) {
  const location      = useLocation();
  const navigate      = useNavigate();
  const service       = getServiceByRoute(location.pathname);
  const access        = usePlanAccess(service);
  const comingSoon    = useComingSoon(service?.slug);
  const paywallFired  = useRef(false);

  useEffect(() => {
    if (!service) return;
    // Coming soon → send to public landing page (with Em breve badge)
    if (comingSoon === true) {
      navigate(`/servicos/${service.slug}`, { replace: true });
    }
  }, [comingSoon, service, navigate]);

  // Track paywall hit once per service/route
  useEffect(() => {
    if (access === 'denied' && service && !paywallFired.current) {
      paywallFired.current = true;
      ph.paywallHit(service.slug, location.pathname);
    }
    if (access !== 'denied') {
      paywallFired.current = false;
    }
  }, [access, service, location.pathname]);

  if (access === 'loading') return <GateSkeleton />;

  // Denied: render the service landing page inside the current layout (no redirect)
  if (access === 'denied' && service) {
    return <ServiceLandingPage slug={service.slug} />;
  }

  if (comingSoon === true && service) return null;
  return <>{children}</>;
}

/** Route layout variant, use as `<Route element={<ServiceGateOutlet />}>` */
export function ServiceGateOutlet() {
  return <ServiceGate><Outlet /></ServiceGate>;
}

// ─── Minimal skeleton while auth/profile is loading ──────────────
function GateSkeleton() {
  return (
    <div className="page-container" style={{ padding: '2rem 1.5rem' }}>
      <div className="shimmer" style={{ height: '28px', width: '60%', marginBottom: '1rem', borderRadius: 'var(--radius-sm)' }} />
      <div className="shimmer" style={{ height: '16px', width: '90%', marginBottom: '0.5rem', borderRadius: 'var(--radius-sm)' }} />
      <div className="shimmer" style={{ height: '16px', width: '80%', marginBottom: '0.5rem', borderRadius: 'var(--radius-sm)' }} />
      <div className="shimmer" style={{ height: '16px', width: '70%', borderRadius: 'var(--radius-sm)' }} />
    </div>
  );
}
